import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import styled from "styled-components"
import { CalculationOutput } from "components/ui/display/CalculationOutput";
import { Table } from "components/ui/display/Table";
import { useEffect, useState } from "react";
import { useErrors } from "providers/ErrorsProvider";
import { calculateClothoide } from "./Clothoide.calculate";
import { Input } from 'components/ui/inputs/Input';
import { calculationLimits } from "store/calculation.config";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";
import { useCustomSearchParams } from "utilities/searchParamsHandler";

export const Clothoide = () => {

    const {customSearchParams, setCustomSearchParams} = useCustomSearchParams();
    const {setError, unsetError} = useErrors();
    const [inputValues, setInputValues] = useState({
        designClass: '',
        speedlimit: '',
        firstRadius: '',
        secondRadius: '',
        maxSuperelevation: ''
    });

    useEffect(() => {
        const initialParams = customSearchParams;
        let initialValues = {};
        if(initialParams.roadClass) initialValues.designClass = initialParams.roadClass;
        if(initialParams.v) initialValues.speedlimit = initialParams.v;
        if(initialParams.r1) initialValues.firstRadius = initialParams.r1;
        if(initialParams.r2) initialValues.secondRadius = initialParams.r2;
        if(initialParams.e) initialValues.maxSuperelevation = initialParams.e;

        console.log(initialValues);
        setInputValues(prev => ({...prev, ...initialValues}));
        onBlur();
        // eslint-disable-next-line
    }, [])

    const [inputVersion, setInputVersion] = useState(0);
    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null
    })

    const onBlur = () => {
        setInputVersion(prev => prev + 1);
    };

    useEffect(() => {
        const context = calculateClothoide(inputValues);
        console.log(context);

        setInputValues(prev => {
            const updates = {};

            for (const [key, obj] of Object.entries(context.input)) {
                if (!context.wasNormalized?.[key]) {
                    continue;
                }

                const value = obj?.value;
                updates[key] = value == null ? "" : String(value);
            }

            return {
                ...prev,
                ...updates
            };
        });

        setCustomSearchParams({
            roadClass: context.input?.designClass?.value?.id,
            v: context.input?.speedlimit?.value,
            r1: context.input?.firstRadius?.value,
            r2: context.input?.secondRadius?.value,
            e: context.input?.maxSuperelevation?.value,
        });
        
        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });
        // eslint-disable-next-line
    }, [inputVersion]);

    return (
        <StyledClothoide>
            <ToolsColumn className="inputColumn">
                <ToolsContainer title='Inndata'>
                    <DropdownMenu
                        label="Dimensjoneringsklasse"
                        defaultValue={inputValues.designClass}
                        defaultSelected={inputValues.designClass}
                        defaultIcon={<MaterialIcon name='road' weight={300}/>}
                        defaultName='Velg dimensjoneringsklasse'
                        onSelect={(e) => {
                            setInputValues(prev => ({...prev, designClass: e}));
                            onBlur();
                        }}
                    >
                        <DropdownOption value='h1-80' name='H1 (80km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h1-90' name='H1 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h2-90' name='H2 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-90' name='H3 (90km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-100' name='H3 (100km/t) - Nasjonal hovedveg'/>
                        <DropdownOption value='h3-110' name='H3 (110km/t) - Nasjonal hovedveg'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={inputValues.speedlimit}
                        onChange={(e) => setInputValues(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={onBlur}
                    />

                    <Input
                        title="Første radius"
                        prefix={<span>R<sub>h,1</sub></span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.firstRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.firstRadius?.severity === 'warning' ?? null}
                        value={inputValues.firstRadius}
                        onChange={(e) => setInputValues(prev => ({...prev, firstRadius: e.target.value}))}
                        onBlur={onBlur}
                    />
                    <Input
                        title="Andre radius"
                        prefix={<span>R<sub>h,2</sub></span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.secondRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.secondRadius?.severity === 'warning' ?? null}
                        value={inputValues.secondRadius}
                        onChange={(e) => setInputValues(prev => ({...prev, secondRadius: e.target.value}))}
                        onBlur={onBlur}
                    />
                    <Input
                        title="Maksimal overhøyde"
                        prefix={<span>e<sub>max</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.maxSuperelevation?.severity === 'error' ?? null}
                        warning={calculatedState.input?.maxSuperelevation?.severity === 'warning' ?? null}
                        value={inputValues.maxSuperelevation}
                        onChange={(e) => setInputValues(prev => ({...prev, maxSuperelevation: e.target.value}))}
                        onBlur={onBlur}
                    />
                </ToolsContainer>
                <ToolsContainer title="Faste variabler">
                    <Input
                        title="Hjulavstand"
                        prefix={<span>b</span>}
                        sufix='m'
                        value={calculatedState.derived?.wheelbase?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Relativ vertikalfart"
                        prefix={<span>v<sub>vf</sub></span>}
                        sufix='m/m'
                        value={calculatedState.derived?.relativeVerticalSpeed?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fartstillegg"
                        prefix={<span>∆ v<sub>t</sub></span>}
                        sufix='km/t'
                        value={calculatedState.derived?.speedAddition?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fartsprofiltillegg"
                        prefix={<span>∆ v<sub>pt</sub></span>}
                        sufix='km/t'
                        value={calculatedState.derived?.speedProfileAddition?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Lengde på overhøydeoppbygging"
                    prefix={<span>L<sub>0,min</sub></span>}
                    value={calculatedState.output?.superelevationRunoffLength?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Klotoideparameter"
                    prefix={<span>A<sub>min</sub></span>}
                    value={calculatedState.output?.clothoideParameter?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Kurveforskyvning"
                    prefix={<span>∆D</span>}
                    value={calculatedState.output?.curveDisplacement?.value?.toFixed(3) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Kjøretid i klotoide"
                    prefix={<span>t</span>}
                    value={calculatedState.output?.drivingTime?.value?.toFixed(3) ?? '--'}
                    sufix='s'
                />
                
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.input?.designClass?.valid ?
                    <ToolsContainer title="Dimensjoneringstabell">
                        <Table
                            headers={[
                                {key: 'minRh', label: <span>R<sub>h,min</sub></span>},
                                {key: 'minA', label: <span>A<sub>min</sub></span>},
                                {key: 'minRvCrest', label: <span>R<sub>v,høy,min</sub></span>},
                                {key: 'minRvSag', label: <span>R<sub>v,lav,min</sub></span>},
                                {key: 'e', label: <span>e</span>, unit: '[m/m]'},
                                {key: 's', label: <span>s</span>, unit: '[m/m]'},
                            ]}
                            data={calculatedState.input.designClass.value.designTable}
                        />
                    </ToolsContainer>

                    : null
                }
                
            </ToolsColumn>
        </StyledClothoide>
    )
}

const StyledClothoide = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: auto;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }
`

